import type { DesktopNotificationNativeState } from '@/lib/desktop-notification-contract';
import type { DesktopNotificationAvailability } from '@/lib/desktop-notifications';
import type { BrowserNotificationPermissionStatus } from './web-push-client';

export type DesktopNotificationUnavailableReason =
  | 'update_required'
  | 'os_unsupported';

export type DesktopNotificationStatus = {
  status: BrowserNotificationPermissionStatus;
  pending: boolean;
  unavailableReason: DesktopNotificationUnavailableReason | null;
  /** Which control the setting offers. */
  action: 'toggle' | 'open_settings' | 'none';
};

/**
 * Desktop notification state for the shared setting. The OS decides whether
 * the app may notify; the existing per-device opt-out decides whether it
 * should. Older desktop builds without the bridge report unavailable rather
 * than a false enabled state.
 */
export function resolveDesktopNotificationStatus(input: {
  availability: Exclude<DesktopNotificationAvailability, 'browser'>;
  nativeState: DesktopNotificationNativeState | null;
  optedOut: boolean;
  canOpenSettings: boolean;
}): DesktopNotificationStatus {
  if (input.availability === 'desktop_outdated')
    return {
      status: 'unsupported',
      pending: false,
      unavailableReason: 'update_required',
      action: 'none',
    };
  if (input.nativeState === null)
    return {
      status: 'not_enabled',
      pending: true,
      unavailableReason: null,
      action: 'none',
    };
  if (input.nativeState === 'unsupported')
    return {
      status: 'unsupported',
      pending: false,
      unavailableReason: 'os_unsupported',
      action: 'none',
    };
  if (input.nativeState === 'blocked')
    return {
      status: 'blocked',
      pending: false,
      unavailableReason: null,
      action: input.canOpenSettings ? 'open_settings' : 'none',
    };
  return {
    status: input.optedOut ? 'not_enabled' : 'enabled',
    pending: false,
    unavailableReason: null,
    action: 'toggle',
  };
}

/** Badge mirrors unread only while desktop notifications are on. */
export function desktopNotificationBadge(
  enabled: boolean,
  unreadCount: number | undefined
): number {
  if (!enabled || unreadCount === undefined) return 0;
  return Math.max(0, Math.min(999, Math.floor(unreadCount)));
}
